import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Grid, Box, Typography } from '@mui/material';
import type { AppDispatch } from '../../store/store';
import { selectAllAlerts, removeExitingAlert, stabilizeNewAlerts, completeNewAlertFlash, type Alert } from '../../store/slices/alertsSlice';
import AnimatedAlertCard from './AnimatedAlertCard';
import ComputerIcon from '@mui/icons-material/Computer';
import { alpha } from '@mui/material/styles';

interface AlertsContainerProps {
  severityFilter: string;
  expandedCards: Set<string>;
  onToggleExpand: (alertId: string) => void;
  groupByHostname: boolean;
  searchQuery: string;
}

const AlertsContainer: React.FC<AlertsContainerProps> = ({
  severityFilter,
  expandedCards,
  onToggleExpand,
  groupByHostname,
  searchQuery,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const alerts = useSelector(selectAllAlerts);

  // Get severity priority for sorting
  const getSeverityPriority = (severity: string): number => {
    switch (severity.toLowerCase()) {
      case 'critical':
        return 4;
      case 'high':
        return 3;
      case 'medium':
        return 2;
      case 'low':
        return 1;
      default:
        return 0;
    }
  };

  // Format relative time for search
  const formatRelativeTime = (timestamp: string) => {
    const now = new Date();
    const alertTime = new Date(timestamp);
    const diffMinutes = Math.floor((now.getTime() - alertTime.getTime()) / (1000 * 60));
    if (diffMinutes < 1) return 'Just now';
    if (diffMinutes < 60) return `${diffMinutes}m ago`;
    if (diffMinutes < 1440) return `${Math.floor(diffMinutes / 60)}h ago`;
    return `${Math.floor(diffMinutes / 1440)}d ago`;
  };

  // Extract hostname from alert (either from metadata or title)
  const getHostname = (alert: Alert): string => {
    // First try metadata
    if (alert.metadata?.hostname) {
      return alert.metadata.hostname;
    }

    // Try to extract from title - pattern: "... (hostname)"
    const match = alert.title.match(/\(([^)]+)\)$/);
    if (match && match[1]) {
      return match[1];
    }

    return 'Unknown';
  };

  // Search filter function
  const matchesSearch = (alert: Alert): boolean => {
    if (!searchQuery.trim()) return true;

    const query = searchQuery.toLowerCase();
    const hostname = getHostname(alert).toLowerCase();
    const searchableText = [
      alert.title.toLowerCase(),
      alert.description?.toLowerCase() || '',
      hostname,
      alert.severity.toLowerCase(),
      alert.category.toLowerCase(),
      formatRelativeTime(alert.createdAt).toLowerCase(),
      new Date(alert.createdAt).toLocaleString().toLowerCase(),
      alert.character?.name?.toLowerCase() || '',
      alert.item?.name?.toLowerCase() || '',
    ].join(' ');

    return searchableText.includes(query);
  };

  // Filter by severity and search, then sort
  const filteredAlerts = alerts
    .filter(alert => severityFilter === 'all' || alert.severity.toLowerCase() === severityFilter)
    .filter(matchesSearch)
    .sort((a, b) => {
      // Primary sort: by severity (highest priority first)
      const severityDiff = getSeverityPriority(b.severity) - getSeverityPriority(a.severity);
      if (severityDiff !== 0) return severityDiff;

      // Secondary sort: by oldest first (ascending timestamp)
      return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
    });

  // Handle exit animation completion
  const handleExitComplete = useCallback(
    (alertId: string) => {
      dispatch(removeExitingAlert(alertId));
    },
    [dispatch],
  );

  // Handle enter animation completion
  const handleEnterComplete = useCallback(() => {
    // After enter animation, briefly flash the alert as "new"
    setTimeout(() => {
      dispatch(stabilizeNewAlerts());
      // Then complete the flash after a short delay
      setTimeout(() => {
        dispatch(completeNewAlertFlash());
      }, 1500); // 1.5 second flash duration
    }, 100);
  }, [dispatch]);

  // Group alerts by hostname if enabled
  const groupedAlerts = groupByHostname
    ? filteredAlerts.reduce(
        (groups, alert) => {
          const hostname = getHostname(alert);
          if (!groups[hostname]) {
            groups[hostname] = [];
          }
          groups[hostname].push(alert);
          return groups;
        },
        {} as Record<string, Alert[]>,
      )
    : { 'All Alerts': filteredAlerts };

  // Render alerts (grouped or ungrouped)
  if (!groupByHostname) {
    return (
      <Grid container spacing={3}>
        {filteredAlerts.map((alert: Alert, index: number) => (
          <Grid size={{ xs: 12, md: 6, lg: 4 }} key={alert.id}>
            <AnimatedAlertCard
              alert={alert}
              index={index}
              onToggleExpand={onToggleExpand}
              isExpanded={expandedCards.has(alert.id)}
              onExitComplete={handleExitComplete}
              onEnterComplete={handleEnterComplete}
            />
          </Grid>
        ))}
      </Grid>
    );
  }

  // Render grouped alerts
  return (
    <Box>
      {Object.entries(groupedAlerts)
        .sort(([a], [b]) => {
          // Sort groups: put "Unknown" last
          if (a === 'Unknown') return 1;
          if (b === 'Unknown') return -1;
          return a.localeCompare(b);
        })
        .map(([hostname, hostnameAlerts]) => (
          <Box key={hostname} sx={{ mb: 4 }}>
            {/* Hostname Header */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                mb: 2,
                pb: 1,
                borderBottom: `2px solid ${alpha('#E31B23', 0.3)}`,
              }}
            >
              <ComputerIcon sx={{ fontSize: 28, color: 'primary.main' }} />
              <Typography
                variant='h6'
                sx={{
                  fontWeight: 600,
                  color: 'text.primary',
                }}
              >
                {hostname}
              </Typography>
              <Typography
                variant='body2'
                sx={{
                  color: 'text.secondary',
                  ml: 'auto',
                }}
              >
                {hostnameAlerts.length} {hostnameAlerts.length === 1 ? 'alert' : 'alerts'}
              </Typography>
            </Box>

            {/* Alerts in this group */}
            <Grid container spacing={3}>
              {hostnameAlerts.map((alert: Alert, index: number) => (
                <Grid size={{ xs: 12, md: 6, lg: 4 }} key={alert.id}>
                  <AnimatedAlertCard
                    alert={alert}
                    index={index}
                    onToggleExpand={onToggleExpand}
                    isExpanded={expandedCards.has(alert.id)}
                    onExitComplete={handleExitComplete}
                    onEnterComplete={handleEnterComplete}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        ))}
    </Box>
  );
};

export default AlertsContainer;
